import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/shared/lib/utils";
import styles from "./Menu.module.scss";
const POSITION_CLASS = {
    "bottom-start": styles.posBottomStart,
    "bottom-end": styles.posBottomEnd,
    "top-start": styles.posTopStart,
    "top-end": styles.posTopEnd,
    "right-start": styles.posRightStart,
    "right-end": styles.posRightEnd,
};
/**
 * Click-triggered dropdown menu. The trigger is cloned with a new `onClick`
 * that toggles open state — the consumer's original `onClick` (if any) is
 * preserved and called first.
 *
 * Keyboard: ArrowDown/Up move focus across enabled items (skips dividers
 * and disabled rows); Enter activates the focused item; Esc closes and
 * returns focus to the trigger.
 */
export const Menu = ({ trigger, items, position = "bottom-end", minWidth, className, portal = false, }) => {
    const [open, setOpen] = React.useState(false);
    const rootRef = React.useRef(null);
    const triggerRef = React.useRef(null);
    const dropdownRef = React.useRef(null);
    const itemRefs = React.useRef([]);
    const [focusIndex, setFocusIndex] = React.useState(-1);
    // fixed-координаты дропдауна в portal-режиме (иначе null → обычный absolute).
    const [portalStyle, setPortalStyle] = React.useState(null);
    // Indices of enabled (non-divider, non-disabled) items, used to skip over
    // dividers/disabled rows during keyboard navigation.
    const enabledIndices = React.useMemo(() => {
        const out = [];
        items.forEach((it, i) => {
            if (it !== "divider" && !it.disabled)
                out.push(i);
        });
        return out;
    }, [items]);
    const close = React.useCallback(() => {
        setOpen(false);
        setFocusIndex(-1);
        triggerRef.current?.focus();
    }, []);
    // Click-outside + ESC. Bound only while open so we don't waste listeners.
    React.useEffect(() => {
        if (!open)
            return;
        const onDown = (e) => {
            const t = e.target;
            // В portal-режиме дропдаун живёт вне root — учитываем и его ref, иначе
            // клик по пункту закрыл бы меню на mousedown до срабатывания onClick.
            if (!rootRef.current?.contains(t) && !dropdownRef.current?.contains(t)) {
                setOpen(false);
                setFocusIndex(-1);
            }
        };
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                close();
            }
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, close]);
    // Portal-режим: считаем fixed-координаты дропдауна от триггера при открытии,
    // пересчитываем на скролл/resize (триггер может ехать вместе с сайдбаром).
    React.useLayoutEffect(() => {
        if (!portal || !open)
            return;
        const place = () => {
            const el = triggerRef.current;
            if (!el)
                return;
            const r = el.getBoundingClientRect();
            const gap = 8;
            const s = { position: "fixed" };
            switch (position) {
                case "right-start":
                    s.left = r.right + gap;
                    s.top = r.top;
                    break;
                case "right-end":
                    s.left = r.right + gap;
                    s.bottom = window.innerHeight - r.bottom;
                    break;
                case "top-start":
                    s.left = r.left;
                    s.bottom = window.innerHeight - r.top + 4;
                    break;
                case "top-end":
                    s.right = window.innerWidth - r.right;
                    s.bottom = window.innerHeight - r.top + 4;
                    break;
                case "bottom-end":
                    s.right = window.innerWidth - r.right;
                    s.top = r.bottom + 4;
                    break;
                default: // bottom-start
                    s.left = r.left;
                    s.top = r.bottom + 4;
                    break;
            }
            setPortalStyle(s);
        };
        place();
        window.addEventListener("scroll", place, true);
        window.addEventListener("resize", place);
        return () => {
            window.removeEventListener("scroll", place, true);
            window.removeEventListener("resize", place);
        };
    }, [portal, open, position]);
    // Move actual DOM focus to the focused item when it changes.
    React.useEffect(() => {
        if (!open || focusIndex < 0)
            return;
        itemRefs.current[focusIndex]?.focus();
    }, [open, focusIndex]);
    const handleTriggerClick = (e) => {
        // Preserve any onClick the consumer attached to the original trigger.
        const orig = trigger.props
            .onClick;
        orig?.(e);
        if (e.defaultPrevented)
            return;
        setOpen((v) => !v);
    };
    const handleListKey = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            const pos = enabledIndices.indexOf(focusIndex);
            const next = pos < 0 ? enabledIndices[0] : enabledIndices[(pos + 1) % enabledIndices.length];
            if (next != null)
                setFocusIndex(next);
        }
        else if (e.key === "ArrowUp") {
            e.preventDefault();
            const pos = enabledIndices.indexOf(focusIndex);
            const next = pos <= 0
                ? enabledIndices[enabledIndices.length - 1]
                : enabledIndices[pos - 1];
            if (next != null)
                setFocusIndex(next);
        }
        else if (e.key === "Home") {
            e.preventDefault();
            setFocusIndex(enabledIndices[0] ?? -1);
        }
        else if (e.key === "End") {
            e.preventDefault();
            setFocusIndex(enabledIndices[enabledIndices.length - 1] ?? -1);
        }
        else if (e.key === "Tab") {
            // Tab out closes the menu.
            setOpen(false);
            setFocusIndex(-1);
        }
    };
    // Open with focus on first enabled item so keyboard users land somewhere.
    const handleOpenSetup = () => {
        if (!open) {
            setFocusIndex(enabledIndices[0] ?? -1);
        }
    };
    const triggerEl = React.cloneElement(trigger, {
        ref: triggerRef,
        "aria-haspopup": "menu",
        "aria-expanded": open,
        onClick: (e) => {
            handleOpenSetup();
            handleTriggerClick(e);
        },
    });
    const dropdown = (_jsx("ul", { ref: dropdownRef, role: "menu", className: cn(styles.dropdown, !portal && POSITION_CLASS[position], open && styles.open), style: {
            ...(portal ? portalStyle ?? { position: "fixed" } : null),
            ...(minWidth ? { minWidth } : null),
        }, onKeyDown: handleListKey, "aria-hidden": !open, children: items.map((it, i) => {
            if (it === "divider") {
                return _jsx("li", { className: styles.divider, role: "separator" }, `d-${i}`);
            }
            return (_jsx("li", { role: "none", children: _jsxs("button", { ref: (el) => {
                        itemRefs.current[i] = el;
                    }, type: "button", role: "menuitem", tabIndex: open ? 0 : -1, disabled: it.disabled, className: cn(styles.item, it.danger && styles.itemDanger, it.accent && styles.itemAccent, it.highlighted && styles.itemHighlight, focusIndex === i && styles.focused), onClick: (e) => {
                        e.stopPropagation();
                        if (it.disabled)
                            return;
                        it.onClick?.();
                        close();
                    }, children: [it.icon && _jsx("span", { className: styles.itemIcon, children: it.icon }), _jsx("span", { className: styles.itemLabel, children: it.label })] }) }, it.id ?? i));
        }) }));
    return (_jsxs("div", { ref: rootRef, className: cn(styles.root, className), children: [triggerEl, portal ? createPortal(dropdown, document.body) : dropdown] }));
};
